<script setup lang="ts">
import type { Schemas } from "#shopware";
import { cmsSectionIncludes } from '~/utils/includes/cms'
import { categoryIncludes } from '~/utils/includes/category'
import { stripCmsPayload } from '~/utils/cmsPayloadStrip'

const props = defineProps<{
    navigationId: string;
}>();

const { search } = useCategorySearch();
const route = useRoute();

const { data: categoryResponse, error } = await useAsyncData(
    "cmsNavigation" + props.navigationId,
    async () => {
        const result = await search(props.navigationId, {
            withCmsAssociations: true,
            query: {
                ...route.query,
                includes: {
                    ...cmsSectionIncludes,
                    ...categoryIncludes
                },
            }
        });
        return stripCmsPayload(result);
    },
);

if (!categoryResponse.value) {
    console.error('[FrontendNavigationPage.vue] category not found', error.value?.message)
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true
    })
}

if (categoryResponse?.value?.extensions?.breadcrumb) {
    const rawBreadcrumb = categoryResponse.value.extensions.breadcrumb
    const categoryBreadcrumb: Breadcrumb[] = Object.entries(rawBreadcrumb)
        .filter(([key]) => key !== 'apiAlias')
        .map(([, entry]) => entry as { name: string; path: string })
    const { clearBreadcrumbs } = useBreadcrumbs(categoryBreadcrumb)

    onBeforeRouteLeave(() => {
        clearBreadcrumbs()
    })
}

// Empty-ref fallback after a redirect: setup keeps running while the 302 response is in flight;
// passing a non-null ref keeps useCategory from throwing and clobbering the redirect.
const { category } = useCategory(
    (categoryResponse.value ? categoryResponse : ref({})) as Ref<Schemas["Category"]>,
);

useHead({
    title: category.value?.metaTitle ? category.value?.metaTitle : category.value?.name,
    meta: [
        { name: 'description', content: category.value?.metaDescription },
        {
            name: 'robots',
            content: (category.value?.translated?.customFields?.prevent_indexing ?? category.value?.customFields?.prevent_indexing) === true ? 'noindex, nofollow' : 'index, follow'
        }
    ]
})
</script>

<template>
    <StructurePage v-if="category.cmsPage" :content="category.cmsPage" />
</template>
